import { BottomSheet } from '@s4l/ds'
import cn from 'classnames'
import React, { useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import DescriptionItem from 'src/comps/DescriptionItem'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { usePage } from 'src/hooks/usePage'
import ButtonLink from '../Atom/Buttons/ButtonLink'
import messages from './messages'
import styles from './styles.module.css'

interface Props {
  description: {
    generic: string
    amenities: string
    dining: string
    renovations: string
    businessAmenities: string
    rooms: string
    attractions: string
    location: string
    general: string
    directions: string
    pool: string
    activity: string
    additional: string
  }
  isFetching?: boolean
  id?: string
  className?: string
}

// eslint-disable-next-line react/display-name
const Description = React.forwardRef<HTMLDivElement, Props>(
  ({ description, isFetching = false, id = '', className }, ref) => {
    const {
      generic,
      amenities,
      dining,
      renovations,
      businessAmenities,
      rooms,
      attractions,
      location,
      general,
      pool,
      activity,
      additional,
    } = description

    const { formatMessage } = useIntl()

    const [isOpen, setIsOpen] = useState(false)
    const descriptionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
    const { pageId } = usePage()

    const { sendEvent } = useKafkaEvent({ page: pageId, eventName: 'open_description' })
    const blocks = useMemo(
      () => {
        return [
          { key: 'generic', content: generic },
          { key: 'rooms', content: rooms },
          { key: 'amenities', content: amenities },
          { key: 'businessAmenities', content: businessAmenities },
          { key: 'location', content: location },
          { key: 'attractions', content: attractions },
          { key: 'dining', content: dining },
          { key: 'pool', content: pool },
          { key: 'activity', content: activity },
          { key: 'renovations', content: renovations },
          { key: 'additional', content: additional },
          { key: 'general', content: general },
        ].filter(item => item.content)
      },
      [description] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const itemsToShow = blocks.slice(0, 2)
    const otherKeys = blocks.slice(2)

    if (isFetching || !description) {
      return null
    }

    const openDescription = (key: string) => {
      setIsOpen(true)
      requestAnimationFrame(() => {
        descriptionRefs.current[key]?.scrollIntoView({ block: 'end' })
      })

      sendEvent({ section: SECTIONS.HOTEL_DESCRIPTION }, { item: key })
    }

    const descriptionToRender = (items: typeof blocks, hasStickyTitles = false) => {
      return items.map(({ key, content }) => {
        return content ? (
          <DescriptionItem
            key={key}
            hasStickyTitle={hasStickyTitles}
            ref={el => {
              descriptionRefs.current[key] = el
            }}
            title={
              messages?.[key as keyof typeof messages] ? (
                <FormattedMessage {...messages[key as keyof typeof messages]} />
              ) : null
            }
          >
            {content}
          </DescriptionItem>
        ) : null
      })
    }

    return (
      <section
        id={id}
        className={cn(className, styles.description)}
        ref={ref}
        data-testid="hotel-details"
      >
        {descriptionToRender(itemsToShow)}
        {otherKeys.length > 0 && (
          <div className={styles.descriptionButtonWrapper}>
            {otherKeys.map(({ key, content }) => {
              return content && messages[key as keyof typeof messages]?.id ? (
                <ButtonLink
                  key={key}
                  onClick={() => openDescription(key)}
                  text={messages[key as keyof typeof messages].id}
                />
              ) : null
            })}
          </div>
        )}

        <BottomSheet open={isOpen} onClose={() => setIsOpen(false)}>
          <BottomSheet.Content
            title={formatMessage({ id: 'global.description' })}
            modalOnDesktop
            scroll
          >
            {descriptionToRender(blocks, true)}
          </BottomSheet.Content>
        </BottomSheet>
      </section>
    )
  }
)

export default Description
